import React, { useState, useEffect } from "react";
import { useAdmin } from "../../context/AdminContext";
import { Save, AlertCircle, CheckCircle, Plus, X } from "lucide-react";

const AboutInfoManager = () => {
  const { adminAPI } = useAdmin();
  const [aboutInfo, setAboutInfo] = useState({
    hero_title: "",
    hero_subtitle: "",
    experience: "",
    projects_count: "",
    specialty: "",
    description: "",
    mission: "",
    expertise_areas: [],
    trust_reasons: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadAboutInfo();
  }, []);

  const loadAboutInfo = async () => {
    try {
      const response = await adminAPI.getAboutInfo();
      setAboutInfo(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des informations À propos:", error);
      setMessage({ type: "error", text: "Erreur lors du chargement" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAboutInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleArrayChange = (field, index, value) => {
    setAboutInfo(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayItem = (field) => {
    setAboutInfo(prev => ({
      ...prev,
      [field]: [...prev[field], ""]
    }));
  };

  const removeArrayItem = (field, index) => {
    setAboutInfo(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage("");

    try {
      // Filter out empty array items
      const cleanedData = {
        ...aboutInfo,
        expertise_areas: aboutInfo.expertise_areas.filter(item => item.trim() !== ""),
        trust_reasons: aboutInfo.trust_reasons.filter(item => item.trim() !== "")
      };

      await adminAPI.updateAboutInfo(cleanedData);
      setMessage({ type: "success", text: "Informations À propos sauvegardées avec succès !" });
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      setMessage({ type: "error", text: "Erreur lors de la sauvegarde" });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">À Propos</h2>
          <p className="text-gray-600 mt-1">Gérez vos informations personnelles et professionnelles</p>
        </div>
      </div>

      {message && (
        <div className={`mb-6 flex items-center space-x-2 p-4 rounded-lg ${
          message.type === "success" 
            ? "bg-green-50 text-green-800 border border-green-200" 
            : "bg-red-50 text-red-800 border border-red-200"
        }`}>
          {message.type === "success" ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-8">
        {/* Hero Section */}
        <div className="bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Section Héro (Page d'accueil)</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre principal
              </label>
              <input
                type="text"
                name="hero_title"
                value={aboutInfo.hero_title}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                placeholder="Vos projets, nos conseils pour les concrétiser"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sous-titre
              </label>
              <textarea
                name="hero_subtitle"
                value={aboutInfo.hero_subtitle}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                placeholder="Expert en bâtiment avec plus de 15 ans d'expérience..."
              />
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations de base</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expérience
              </label>
              <input
                type="text"
                name="experience"
                value={aboutInfo.experience}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                placeholder="15+ ans"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre de projets
              </label>
              <input
                type="text"
                name="projects_count"
                value={aboutInfo.projects_count}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                placeholder="200+"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Spécialité
              </label>
              <input
                type="text"
                name="specialty"
                value={aboutInfo.specialty}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                placeholder="Rénovation et construction"
                required
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description principale
          </label>
          <textarea
            name="description"
            value={aboutInfo.description}
            onChange={handleInputChange}
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
            placeholder="Décrivez votre parcours et votre passion..."
            required
          />
        </div>

        {/* Mission */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mission
          </label>
          <textarea
            name="mission"
            value={aboutInfo.mission}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
            placeholder="Décrivez votre mission et vos objectifs..."
            required
          />
        </div>

        {/* Expertise Areas */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Domaines d'expertise
            </label>
            <button
              type="button"
              onClick={() => addArrayItem('expertise_areas')}
              className="flex items-center space-x-2 text-blue-900 hover:text-blue-800"
            >
              <Plus className="w-4 h-4" />
              <span>Ajouter</span>
            </button>
          </div>
          <div className="space-y-3">
            {aboutInfo.expertise_areas.map((area, index) => (
              <div key={index} className="flex items-center space-x-3">
                <input
                  type="text"
                  value={area}
                  onChange={(e) => handleArrayChange('expertise_areas', index, e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                  placeholder="Ex: Rénovation complète"
                />
                <button
                  type="button"
                  onClick={() => removeArrayItem('expertise_areas', index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Reasons */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Raisons de faire confiance
            </label>
            <button
              type="button"
              onClick={() => addArrayItem('trust_reasons')}
              className="flex items-center space-x-2 text-blue-900 hover:text-blue-800"
            >
              <Plus className="w-4 h-4" />
              <span>Ajouter</span>
            </button>
          </div>
          <div className="space-y-3">
            {aboutInfo.trust_reasons.map((reason, index) => (
              <div key={index} className="flex items-center space-x-3">
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => handleArrayChange('trust_reasons', index, e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                  placeholder="Ex: Expertise technique reconnue depuis 15 ans"
                />
                <button
                  type="button"
                  onClick={() => removeArrayItem('trust_reasons', index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            type="submit"
            disabled={isSaving}
            className="flex items-center space-x-2 bg-blue-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                <span>Sauvegarde...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Sauvegarder</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AboutInfoManager;